import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { SHARED_ENVIRONMENT } from '@k12/environment';
import { DocumentsList, FileDocumentType } from '@k12/models';
import { catchError, firstValueFrom, map, Observable, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DocumentsService implements OnDestroy {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly sharedEnvironment = inject(SHARED_ENVIRONMENT);
  private readonly pendingRevokeTimeouts = new Set<ReturnType<typeof setTimeout>>();

  ngOnDestroy(): void {
    this.pendingRevokeTimeouts.forEach((id) => clearTimeout(id));
    this.pendingRevokeTimeouts.clear();
  }

  //get url shared environment
  private readonly apiUrl = this.sharedEnvironment.adminApiUrl;

  public async downloadFile(containerName: string, blobPath: string): Promise<void> {
    if (!containerName || !blobPath) {
      throw new Error('containerName and blobPath are required.');
    }

    const endpoint = `${this.apiUrl}/Files/DownloadFile/${containerName.toLowerCase()}`;

    const response = await firstValueFrom(
      this.http.get(endpoint, {
        params: new HttpParams().set('blobPath', blobPath),
        observe: 'response',
        responseType: 'blob'
      })
    );

    const blob = response.body;
    if (!blob) {
      throw new Error('No file content returned.');
    }
    const contentDisposition = response.headers.get('Content-Disposition');

    const blobUrl = URL.createObjectURL(blob);
    // Resolve filename: Get the header's filename from 1 of 2 formats, or if null use the URL filename or if null, use "download".
    const fileNameFromHeader = contentDisposition?.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const rawFileName = fileNameFromHeader?.[1] ?? fileNameFromHeader?.[2];
    const fallbackFileName = blobPath.split('/').pop()?.split('?')[0] || 'download';
    const decodedRawFileName = rawFileName ? this.decodeFileName(rawFileName) : null;
    const resolvedFileName = decodedRawFileName ?? this.decodeFileName(fallbackFileName);
    // Remove timestamps from the filename for preserving original file name while downloading
    const fileName = this.removeTimestampFromFileName(resolvedFileName);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  }

  // Function to remove timestamps from filenames
  private removeTimestampFromFileName(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.');
    const extension = dotIndex > -1 ? fileName.slice(dotIndex) : '';
    const baseName = dotIndex > -1 ? fileName.slice(0, dotIndex) : fileName;

    const cleanedBaseName = baseName
      // Remove suffix like "-2026-06-11" or "_2026-06-11 13:45:00".
      // Remove compact suffix like "-20260611" or "_20260611_134500".
      // Remove one or more compact datetime suffixes like "-20250910173718-20250911212123".
      // Cleanup trailing spaces left after removing suffixes.
      .replace(/[-_ ]\d{4}-\d{2}-\d{2}(?:[ T_-]\d{2}[-:]\d{2}(?:[-:]\d{2})?)?$/, '')
      .replace(/(?:[-_ ]\d{14})+$/, '')
      .replace(/[-_ ]\d{8}(?:[-_ ]?\d{6})?$/, '')
      .trim();

    return `${cleanedBaseName || baseName}${extension}`;
  }

  private decodeFileName(fileName: string): string {
    try {
      return decodeURIComponent(fileName.replaceAll('+', ' '));
    } catch {
      return fileName;
    }
  }

  public getDocumentList(container: string, entityId: string | null = null, accountId?: string | null, collectionType?: string | null): Observable<DocumentsList[]> {
    if (entityId) {
      return this.getDocuments(container, entityId, collectionType);
    }
    return this.getLegalEntityID(<string>accountId).pipe(switchMap(entityIdResponse => this.getDocuments(container, entityIdResponse, collectionType)));
  }

  public getDocumentListWithoutEntity(container: string, collectionType?: string | null): Observable<DocumentsList[]> {
    return this.getDocuments(container, null, collectionType);
  }

  public upload(file: File, application: string, filename: string, collectionType: string, documentType: string): Observable<unknown> {
    const formData = new FormData();
    formData.append('file', file);

    let url = `${this.apiUrl}/Files/FileUpload/${application}`;
    const queryParams: string[] = [];

    if (filename?.trim()) {
      queryParams.push(`BlobName=${encodeURIComponent(filename)}`);
    }

    if (collectionType?.trim()) {
      queryParams.push(`collectionType=${encodeURIComponent(collectionType)}`);
    }

    if (documentType?.trim()) {
      queryParams.push(`documentType=${encodeURIComponent(documentType)}`);
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return this.http.post(url, formData, {
      observe: 'events',
      reportProgress: true
    }).pipe(
      map((response) => {
        return response ?? {};
      }),
      catchError((err) => {
        throw err;
      })
    );
  }

  public viewFile(absoluteUri: string): void {
    fetch(absoluteUri)
      .then(res => res.blob())
      .then(blob => {
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(pdfBlob);
        const newWindow = window.open(blobUrl, '_blank');
        if (!newWindow) {
          const link = document.createElement('a');
          link.href = blobUrl;
          link.target = '_blank';
          link.click();
        }
        const timeoutId = setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
          this.pendingRevokeTimeouts.delete(timeoutId);
        }, 10000);
        this.pendingRevokeTimeouts.add(timeoutId);
      })
      .catch(err => console.error('Failed to view PDF:', err));
  }

  private getLegalEntityID(accountId: string): Observable<string> {
    return this.http.get<string>(
      `${this.apiUrl}/personas/GetProgramPartnerIdByHdmaAccountId/${accountId}`);
  }

  private getDocuments(container: string, legalEntityId: string | null = null, collectionType?: string | null): Observable<DocumentsList[]> {
    let params = new HttpParams();

    if (collectionType && collectionType.trim() !== '') {
      params = params.set('collectionType', collectionType);
    }

    if (legalEntityId && legalEntityId.trim() !== '') {
      params = params.set('legalEntity', legalEntityId);
    }

    return this.http.get<DocumentsList[]>(
      `${this.apiUrl}/Files/GetFiles/${container.toLowerCase()}`, { params }
    ).pipe(
      map((response) => {
        return response ?? [];
      }),
      catchError((err) => {
        console.error(err);
        err.message = 'You are not properly set up for documents.';
        throw err;
      })
    );
  }

  getFileDocumentType(programPartnerTypeId?: number): Observable<FileDocumentType[]> {
    let params = new HttpParams();
    if (programPartnerTypeId) {
      params = params.set('programPartnerTypeId', String(programPartnerTypeId));
    }
    return this.http.get<FileDocumentType[]>(`${this.apiUrl}/documenttypes`, { params });
  }
}